export const baseVertex = /* glsl */ `
uniform sampler2D skGlyphTransformAtlas;
uniform float skGlyphScale;

attribute vec4 skGlyphBoundingBox;
attribute vec4 skGlyphTexCoords;
attribute float skGlyphLabelIndex;

varying vec2 vUv;
varying vec4 vGlyphBoundingBox;

#include <common>
#include <clipping_planes_pars_vertex>

float extractAspectFromProjection() {
  vec4 minPoint = projectionMatrix * vec4(-1, -1, 1, 1);
  vec4 maxPoint = projectionMatrix * vec4( 1,  1, 1, 1);
  
  float width = maxPoint.x - minPoint.x;
  float height = maxPoint.y - minPoint.y;
  
  return width / height;
}

void main() {

	{
	    // Compute the four corners over the UV area unconditionally
	    float uvX = skGlyphTexCoords.x;
	    float uvY = skGlyphTexCoords.y;
	    float uvW = skGlyphTexCoords.z;
	    float uvH = skGlyphTexCoords.w;
	    vec2 p0 = vec2(uvX, uvY);
	    vec2 p1 = vec2(uvX + uvW, uvY);
	    vec2 p2 = vec2(uvX + uvW, uvY + uvH);
	    vec2 p3 = vec2(uvX, uvY + uvH);
	    
	    // Initialize to a default and then conditionally move one of the corners into the output
	    vUv = vec2(0,0);
	    if (gl_VertexID == 0) {
	        vUv = p0;
	    } else if (gl_VertexID == 1) {
	        vUv = p1;
	    } else if (gl_VertexID == 2) {
	        vUv = p2;
	    } else if (gl_VertexID == 3) {
	        vUv = p3;
	    }
	}
	
	// Pass through attributes to fragment shader
	vGlyphBoundingBox = skGlyphBoundingBox;
	
	// Get the size of the texture, always using the highest detail mip (this texture will never have mips)
	vec2 transformAtlasSize = vec2(textureSize(skGlyphTransformAtlas, 0));
	
	// Convert the label index into a position inside the atlas. This needs the width of the texture
	float transformU = trunc(mod(skGlyphLabelIndex, transformAtlasSize.x));
	float transformV = trunc(skGlyphLabelIndex / transformAtlasSize.x);
	
	// The local-space position of the label. 'Local' is defined as relative to the parent label collection. The model
	// view matrix will contain the transform of the label collection.
	//
	// labelPosition will define the anchor position of the label, which will get projected into screen space so we can
	// place the glyph vertices relative to the anchor in screen space.
	vec4 labelPosition = texelFetch(skGlyphTransformAtlas, ivec2(transformU, transformV), 0);
	labelPosition.w = 1.0; // This needs to be one for use with the matrix multiply

    // Get our 3D anchor in view space
	vec4 mvPosition = modelViewMatrix * labelPosition;

    float aspect = extractAspectFromProjection();
    vec2 aspectCorrectedPosition = vec2(position.x * aspect, position.y);
    vec2 aspectCorrectedOffset = vec2(skGlyphBoundingBox.x * aspect, skGlyphBoundingBox.y);
	vec2 quadVertexPosition = (aspectCorrectedPosition * skGlyphBoundingBox.zw * skGlyphScale) + (aspectCorrectedOffset * skGlyphScale); 

	gl_Position = projectionMatrix * mvPosition;
	
	gl_Position.xy += quadVertexPosition * gl_Position.w;
	
	#include <clipping_planes_vertex>

}
`;
export const baseFragment = /* glsl */ `

#include <common>
#include <map_pars_fragment>
#include <clipping_planes_pars_fragment>

uniform float opacity;

// Picking buffer uniforms
uniform uint objectId;
uniform uint extraId;

// Fragment shader payload
varying vec2 vUv;
varying vec4 vGlyphBoundingBox;

uniform float uSkSDFExponent;

uniform vec4 uSkFillColor;
uniform vec4 uSkStrokeColor;
uniform float uSkStrokeWidth;

float skSdfValueToSignedDistance(float alpha) {
  // Inverse of exponential encoding in webgl-sdf-generator
  float maxDimension = max(vGlyphBoundingBox.z, vGlyphBoundingBox.w);
  float absDist = (1.0 - pow(2.0 * (alpha > 0.5 ? 1.0 - alpha : alpha), 1.0 / uSkSDFExponent)) * maxDimension;
  float signedDist = absDist * (alpha > 0.5 ? -1.0 : 1.0);
  return signedDist;
}

float skGetAADist() {
  return vGlyphBoundingBox.z / 32.0;
}

float skGetFragDistValue() {
  return skSdfValueToSignedDistance(texture2D(map, vUv).r);
}

float skGetEdgeAlpha(float distance, float distanceOffset, float aaDist) {
  float alpha = smoothstep(
    distanceOffset + aaDist,
    distanceOffset - aaDist,
    distance
  );

  return alpha;
}

void main() {

	#include <clipping_planes_fragment>
    
	float aaDist = skGetAADist();
    float fragDistance = skGetFragDistValue();
    float edgeAlpha = skGetEdgeAlpha(fragDistance, 0.0, aaDist);

    vec4 fillRGBA = uSkFillColor;

    vec4 strokeRGBA = uSkStrokeWidth == 0.0 ? fillRGBA : uSkStrokeColor;
    
    if (fillRGBA.a == 0.0) fillRGBA.rgb = strokeRGBA.rgb;

    gl_FragColor = mix(fillRGBA, strokeRGBA, smoothstep(
        -uSkStrokeWidth - aaDist,
        -uSkStrokeWidth + aaDist,
        fragDistance
    ));
    gl_FragColor.a *= edgeAlpha;

    if (edgeAlpha == 0.0) {
        discard;
    }
    
    gl_FragColor.a *= opacity;
    
	#include <tonemapping_fragment>
	#include <encodings_fragment>
    
}
`;
